<template>
  <div :class="classes">
    <div v-if="isLoadingFirstTime" class="trbo-a-wizard-page-products__loading">
      <loading-indicator />
    </div>
    <div v-else :class="listClasses">
      <div
        v-for="(item, index) in products"
        :key="index"
        class="trbo-a-wizard-page-products__list__item"
      >
        <products-item
          ref="productsItem"
          class="trbo-a-products-item"
          :item="item"
          :message="message"
          :keep-attribute-matching-expanded="keepAttributeMatchingExpanded"
          :routine-header-height="'300px'"
          :is-add-to-cart-disabled="isAddToCartDisabled"
          @product-clicked="handleProductClickedEvent"
          @product-added-to-cart="handleProductAddedToCartEvent"
          @attribute-matching-list-expanded="handleAttributeMatchingListExpandedEvent"
          @product-info-clicked="handleProductInfoClickedEvent"
        />
      </div>
    </div>
    <no-products v-if="noProducts" @restart="$emit('restart')" />
    <div
      v-if="showAddRoutineToCartButton"
      :class="wizardPageProductsActions"
    >
      <button class="trbo-a-wizard-page-products__actions__button" @click="addRoutineToCart">
        <add-to-cart-icon />
        <span class="trbo-a-wizard-page-products__actions__button__text">
          {{ $t('product.addRoutineToCart') }}
        </span>
      </button>
      <div v-if="isShowingCheckOrSpinnerIcons" class="trbo-a-wizard-page-products__actions__feedback">
        <add-to-cart-check v-if="isShowingCheckIcon" />
        <spinner v-if="isShowingSpinnerIcon" size="small" />
      </div>
    </div>
  </div>
</template>

<script>
// @flow

import { mapActions, mapGetters, mapState } from 'vuex'

import { dispatchChatchampEvent, WIZARD_EVENTS } from '../../../../main/eventTracking'
import { AddToCartStrategy } from '../../../../common/custom'
import { isMobile } from '../../../../common/device'
import { getProductsUtilityAverage, getProductsUtilityMax, productIds } from '../../../../common/models'
import ProductsItem from '../../../../chatbot/components/MessageTypes/ProductsMessage/ProductsItem/ProductsItem.vue'
import AddToCartIcon
from '../../../../chatbot/components/MessageTypes/ProductsMessage/ProductsItem/AddToCartButton/AddToCartIcon.vue'
import AddToCartCheck
from '../../../../chatbot/components/MessageTypes/ProductsMessage/ProductsItem/AddToCartButton/AddToCartCheck.vue'
import Spinner from '../../Spinner/Spinner.vue'

import { LoadingIndicator } from '../../../../common/components'
import NoProducts from './NoProducts.vue'

export default {
  components: {
    ProductsItem,
    LoadingIndicator,
    AddToCartIcon,
    AddToCartCheck,
    Spinner,
    NoProducts
  },
  props: {
    isLoadingProducts: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      isLoadingFirstTime: true
    }
  },
  computed: {
    ...mapGetters(['isEnabledAddToCart']),
    ...mapState(['recommendedProducts', 'hiddingProducts', 'sort', 'cartProducts', 'routineAddingToCart']),
    utilityMax (): number {
      return getProductsUtilityMax(this.products)
    },
    utilityAverage (): number {
      return getProductsUtilityAverage(this.products)
    },
    products (): Array<Object> {
      return this.recommendedProducts.elements ?? []
    },
    message (): Object {
      return {
        content: this.recommendedProducts,
        lastMessage: this.isLoadingFirstTime || this.isLoadingProducts
      }
    },
    keepAttributeMatchingExpanded (): boolean {
      return !isMobile()
    },
    isRoutine (): boolean {
      return this.message.content.isRoutine
    },
    classes () {
      return {
        'trbo-a-wizard-page-products': true,
        'trbo-a-wizard-page-products--is-routine': this.isRoutine
      }
    },
    firstProductId (): Object {
      return this.products[0].productId
    },
    listClasses () {
      return {
        'trbo-a-wizard-page-products__list': true,
        'trbo-a-wizard-page-products__list--leaving': this.hiddingProducts
      }
    },
    isAddToCartDisabled (): boolean {
      return /.*\.(chatchamp\.com)/.test(window.location.hostname)
    },
    showAddRoutineToCartButton (): boolean {
      const { isAddRoutineToCartEnabled } = this.$store.state.recommendedProducts

      return this.isEnabledAddToCart && isAddRoutineToCartEnabled && !this.isAddToCartDisabled && !this.isLoadingFirstTime && this.isRoutine
    },
    routineProductIds (): Array<string> {
      return this.products.map(item => item.productIdentifier)
    },
    wizardPageProductsActions (): Object {
      return {
        'trbo-a-wizard-page-products__actions': true,
        'trbo-a-wizard-page-products__actions--is-disabled': this.isShowingCheckOrSpinnerIcons
      }
    },
    isShowingCheckOrSpinnerIcons (): boolean {
      return this.isShowingCheckIcon || this.isShowingSpinnerIcon
    },
    isShowingCheckIcon (): boolean {
      return this.routineProductIds.every(productId => this.cartProducts.includes(productId))
    },
    isShowingSpinnerIcon (): boolean {
      return this.routineAddingToCart
    },
    noProducts (): boolean {
      return !this.isLoadingFirstTime && this.products.length === 0
    }
  },
  mounted () {
    if (this.products) {
      dispatchChatchampEvent(WIZARD_EVENTS.Completed, {
        customerId: this.$store.state.customerId,
        subscriberId: this.$store.state.subscriberId,
        wizardId: this.$store.state.wizard.id,
        productIds: productIds(this.products),
        products: this.products,
        type: 'products_recommended',
        utilityMax: this.utilityMax,
        utilityAverage: this.utilityAverage
      })
    }
    this.isLoadingFirstTime = false
    window.addEventListener('resize', this.setRoutineHeadersHeight)
    this.$nextTick(() => {
      if (this.hasMoreThanOneColumnPerRow()) {
        this.setRoutineHeadersHeight()
      }
      this.processLogicElementsAfter()
    })
  },
  destroyed () {
    window.removeEventListener('resize', this.setRoutineHeadersHeight)
  },
  methods: {
    ...mapActions(['getRecommendedProducts', 'setProductAddingToCart', 'addProductToCart', 'setRoutineAddingToCart', 'setNotification', 'processLogicElementsAfter']),
    handleProductClickedEvent (eventData): void {
      const product = this.products.find(product => product.productId === eventData.productId)
      dispatchChatchampEvent(WIZARD_EVENTS.RecommendedProductClicked, {
        customerId: this.$store.state.customerId,
        subscriberId: this.$store.state.subscriberId,
        wizardId: this.$store.state.wizard.id,
        productId: eventData.productId,
        utility: product.utility
      })
    },
    async handleProductAddedToCartEvent (eventData): Promise<void> {
      const { customerId, subscriberId } = this.$store.state
      this.setProductAddingToCart(eventData.productId)
      const result = await new AddToCartStrategy('wizard').execute(customerId, subscriberId, [eventData.productId])
      this.setProductAddingToCart(null)
      if (result) {
        this.addProductToCart(eventData.productId)
      } else {
        this.failedAddToCart()
      }
      dispatchChatchampEvent(WIZARD_EVENTS.AddedToCartEvent, {
        customerId,
        subscriberId,
        wizardId: this.$store.state.wizard.id,
        product: eventData.product,
        productId: eventData.productId
      })
    },
    hasMoreThanOneColumnPerRow () {
      return window.innerWidth >= 640
    },
    setRoutineHeadersHeight () {
      if (this.products.length === 0) return
      if (this.hasMoreThanOneColumnPerRow()) {
        const routineHeadersGrouped = this.groupRoutineHeaders()
        routineHeadersGrouped.forEach(routineHeaders => {
          const maxHeight = this.getMaxHeightRoutineHeader(routineHeaders)
          this.setMaxHeightRoutineHeaders(maxHeight, routineHeaders)
        })
      } else {
        this.resetRoutineHeadersHeight()
      }
    },
    groupRoutineHeaders () {
      const columns = window.innerWidth < 1024 ? 2 : 3
      const routineHeadersGrouped = []
      let column = 0
      this.$refs.productsItem.forEach((productsItem, index) => {
        const { productItemRoutineHeader } = productsItem.$refs
        if (productItemRoutineHeader) {
          if (!routineHeadersGrouped[column]) routineHeadersGrouped[column] = []
          routineHeadersGrouped[column].push(productItemRoutineHeader)
          if ((index + 1) % columns === 0) column++
        }
      })
      return routineHeadersGrouped
    },
    resetRoutineHeadersHeight () {
      this.$refs.productsItem.forEach(productsItem => {
        const { productItemRoutineHeader } = productsItem.$refs
        if (productItemRoutineHeader) {
          productItemRoutineHeader.style.height = 'auto'
        }
      })
    },
    getMaxHeightRoutineHeader (routineHeaders) {
      let maxHeight = 0
      routineHeaders.forEach(routineHeader => {
        routineHeader.style.height = 'auto'
        const height = routineHeader.offsetHeight
        if (height > maxHeight) {
          maxHeight = height
        }
      })
      return maxHeight
    },
    setMaxHeightRoutineHeaders (maxHeight, routineHeaders) {
      if (maxHeight) {
        routineHeaders.forEach(routineHeader => {
          routineHeader.style.height = `${maxHeight}px`
        })
      }
    },
    async addRoutineToCart (): Promise<void> {
      const { customerId, subscriberId } = this.$store.state
      this.setRoutineAddingToCart(true)
      const result = await new AddToCartStrategy('wizard').execute(customerId, subscriberId, this.routineProductIds)
      this.setRoutineAddingToCart(false)
      if (result) {
        this.routineProductIds.map(this.addProductToCart)
      } else {
        this.failedAddToCart()
      }
      dispatchChatchampEvent(WIZARD_EVENTS.RoutineAddedToCartEvent, {
        customerId,
        subscriberId,
        wizardId: this.$store.state.wizard.id,
        products: this.products,
        productIds: this.routineProductIds.join(',')
      })
    },
    handleAttributeMatchingListExpandedEvent (eventData: Object): void {
      dispatchChatchampEvent(WIZARD_EVENTS.AttributeMatchingListExpandedEvent, {
        customerId: this.$store.state.customerId,
        subscriberId: this.$store.state.subscriberId,
        wizardId: this.$store.state.wizard.id,
        productId: eventData.productId
      })
    },
    handleProductInfoClickedEvent (eventData: Object): void {
      dispatchChatchampEvent(WIZARD_EVENTS.RecommendedProductInfoClickedEvent, {
        customerId: this.$store.state.customerId,
        subscriberId: this.$store.state.subscriberId,
        wizardId: this.$store.state.wizard.id,
        productId: eventData.productId
      })
    },
    failedAddToCart (): void {
      this.setNotification({ type: 'error', message: this.$t('product.addToCartError') })
    }
  }
}
</script>

<style scoped src="./wizard-page-products.scss" />
